import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HardHat, Loader2, FileDown, ArrowRight, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { getShare, renderImageUrl } from "../lib/api";
import { exportEstimatePDF } from "../lib/pdf";

const money = (n, cur) => `${cur === "CAD" ? "CA$" : "$"}${Math.round(n || 0).toLocaleString("en-US")}`;

const SharedEstimate = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const run = async () => {
      try {
        const d = await getShare(slug);
        setData(d);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-[#1D4ED8]" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 text-center">
        <p className="text-slate-600">This shared estimate could not be found or has expired.</p>
        <Button onClick={() => navigate("/")} className="mt-4 bg-[#DC2626] hover:bg-[#b91c1c] text-white">Go to cost2build.ai</Button>
      </div>
    );
  }

  const { request: r, result: res } = data;
  const cur = res.currency || "USD";

  const emailEstimate = () => {
    const subject = `Construction estimate: ${data.name || r.building_type}`;
    const body = [
      `Hi,`,
      ``,
      `Here's a construction cost estimate I put together on cost2build.ai:`,
      ``,
      `Project: ${data.name || r.building_type}`,
      `Type: ${r.building_type} · ${r.size_sqft} sq ft · ${r.finish_level} finish`,
      `Location: ${r.region}, ${r.country}`,
      `Estimated total: ${money(res.total_low, cur)} – ${money(res.total_high, cur)} ${cur}`,
      ``,
      `Full interactive estimate: ${window.location.href}`,
      ``,
      `Note: AI-generated ballpark, not a substitute for a licensed contractor quote.`,
    ].join("\n");
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1D4ED8] text-white"><HardHat className="h-5 w-5" /></span>
            <span className="font-display text-xl font-700 text-[#0A2540]">cost<span className="text-[#DC2626]">2</span>build<span className="text-[#1D4ED8]">.ai</span></span>
          </button>
          <span className="text-sm text-slate-500">Shared estimate</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          {data.render_id && (
            <img src={renderImageUrl(data.render_id)} alt="render" className="w-full h-64 object-cover" />
          )}
          <div className="p-7">
            <h1 className="font-display text-2xl font-700 text-[#0A2540]">{data.name || `${r.building_type} Estimate`}</h1>
            <p className="mt-1 text-slate-500 text-sm">{r.building_type} · {r.size_sqft} sq ft · {r.finish_level} · {r.region} · {r.country}</p>

            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-5 flex items-center justify-between">
              <span className="text-slate-500 text-sm">Estimated total range</span>
              <span className="font-display text-2xl font-700 text-[#0A2540]">{money(res.total_low, cur)} – {money(res.total_high, cur)}</span>
            </div>

            {res.summary && <p className="mt-5 text-slate-600 leading-relaxed">{res.summary}</p>}

            <div className="mt-6 rounded-lg border border-slate-200 overflow-hidden">
              <div className="grid grid-cols-12 bg-slate-50 px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-500">
                <span className="col-span-6">Scope</span>
                <span className="col-span-3 text-right">Material</span>
                <span className="col-span-3 text-right">Labor</span>
              </div>
              <div className="divide-y divide-slate-100">
                {(res.scopes || []).map((s, i) => (
                  <div key={i} className="grid grid-cols-12 px-4 py-2.5 text-sm">
                    <span className="col-span-6 text-[#0A2540]">{s.name}</span>
                    <span className="col-span-3 text-right text-slate-600">{money(s.material, cur)}</span>
                    <span className="col-span-3 text-right text-slate-600">{money(s.labor, cur)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => exportEstimatePDF({ request: r, result: res, name: data.name })} variant="outline" className="border-slate-300 text-[#0A2540] hover:border-[#1D4ED8] hover:text-[#1D4ED8]">
                <FileDown className="h-4 w-4 mr-2" /> Download PDF
              </Button>
              <Button onClick={emailEstimate} variant="outline" className="border-slate-300 text-[#0A2540] hover:border-[#1D4ED8] hover:text-[#1D4ED8]">
                <Mail className="h-4 w-4 mr-2" /> Email to contractor
              </Button>
              <Button onClick={() => navigate("/")} className="bg-[#DC2626] hover:bg-[#b91c1c] text-white group">
                Make your own estimate <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <p className="mt-4 text-xs text-slate-400">AI-generated ballpark · not a substitute for a licensed contractor quote.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SharedEstimate;
